<template>
  <div>
    <div class="container">
      <div class="box-title">
        <h4><i class="fal fa-file-export"></i> Export Excel</h4>
        <vs-button flat size="small" @click="fetchList" :loading="isListLoading"><i class="fal fa-sync-alt"></i> ໂຫລດໃໝ່</vs-button>
      </div>

      <!-- Request form -->
      <div class="box">
        <div class="columns">
          <div class="column is-3">
            <label class="label">ປະເພດລາຍງານ</label>
            <div class="select is-fullwidth">
              <select v-model="form.report_type">
                <option value="transactions">ລາຍການເຄື່ອນໄຫວ</option>
                <option value="balance">ຍອດພະນັກງານ</option>
              </select>
            </div>
          </div>
          <div class="column is-3">
            <label class="label">ສາຂາ</label>
            <BranchSelect v-model="form.branch_id" placeholder="ທັງໝົດ / ຕາມສິດ" />
          </div>
          <div class="column is-2">
            <label class="label">ແຕ່ວັນທີ</label>
            <input type="date" class="input" v-model="form.from_date" />
          </div>
          <div class="column is-2">
            <label class="label">ຫາວັນທີ</label>
            <input type="date" class="input" v-model="form.to_date" />
          </div>
          <div class="column is-2 apply-col">
            <vs-button @click="requestExport" :loading="isRequesting"><i class="fal fa-plus"></i> ສ້າງ</vs-button>
          </div>
        </div>
        <p class="cap-hint">* ໄລຍະວັນທີສູງສຸດ 92 ວັນ. ໄຟລ໌ຈະພ້ອມໂຫລດເມື່ອປະມວນຜົນແລ້ວ.</p>
      </div>

      <!-- Export list -->
      <div class="box">
        <table class="table is-fullwidth is-hoverable is-striped">
          <thead>
            <tr><th>ຊື່ໄຟລ໌</th><th>ສະຖານະ</th><th>ສ້າງເມື່ອ</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="f in exports" :key="f.id">
              <td><span class="reference">{{ f.name }}</span></td>
              <td><span class="tag" :class="statusClass(f.status)">{{ statusLabel(f.status) }}</span></td>
              <td><small>{{ formatMonitorDateTime(f.created_at) }}</small></td>
              <td>
                <vs-button v-if="Number(f.status) === 1" flat size="small" @click="download(f.id)" :loading="downloadingId === f.id">
                  <i class="fal fa-download"></i> ໂຫລດ
                </vs-button>
                <span v-else-if="Number(f.status) === 2" class="has-text-danger"><i class="fal fa-times"></i> ລົ້ມເຫລວ</span>
                <span v-else class="has-text-grey"><i class="fal fa-spinner fa-spin"></i> ກຳລັງປະມວນຜົນ</span>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="!exports.length && !isListLoading" class="has-text-centered has-text-grey py-3">ຍັງບໍ່ມີລາຍການສົ່ງອອກ</p>
      </div>
    </div>
  </div>
</template>

<script>
import walletMonitorService from '@/services/walletMonitorService';
import { extractErrorInfo } from '@/services/walletService';
import BranchSelect from '@/components/BranchSelect.vue';
import { formatMonitorDateTime, todayStr } from '@/services/walletMonitorLabels';

const REFRESH_MS = 6000;
const MAX_REFRESH = 40;      // ~4 minutes of auto-refresh while exports are processing, then stop
const MAX_RANGE_DAYS = 92;   // mirrors the BFF export cap

export default {
  name: 'WalletMonitorExport',
  components: { BranchSelect },
  data() {
    return {
      exports: [],
      isListLoading: false,
      isRequesting: false,
      downloadingId: null,
      refreshTimer: null,
      refreshCount: 0,
      alive: true,
      form: { report_type: 'transactions', branch_id: '', from_date: '', to_date: '' },
    };
  },
  mounted() {
    // Search-driven: only prefill the form dates on open. The export list loads on the ໂຫລດໃໝ່ (refresh)
    // button and after a request is created — never auto-queried on open. The branch picker (BranchSelect)
    // loads its own options on demand via server-side search.
    this.setDefaultDates();
  },
  beforeDestroy() {
    this.alive = false;
    this.stopRefresh();
  },
  methods: {
    formatMonitorDateTime,
    setDefaultDates() {
      this.form.from_date = todayStr();
      this.form.to_date = todayStr();
    },
    statusLabel(s) { return { 0: 'ກຳລັງປະມວນຜົນ', 1: 'ພ້ອມ', 2: 'ລົ້ມເຫລວ' }[Number(s)] || s; },
    statusClass(s) { return { 0: 'is-info', 1: 'is-success', 2: 'is-danger' }[Number(s)] || 'is-light'; },
    async fetchList() {
      this.isListLoading = true;
      try {
        const res = await walletMonitorService.listExports({ per_page: 20 });
        if (!this.alive) return;
        const d = res.data || res;
        this.exports = d.exports || [];
        // Keep refreshing while any export is still processing — but bounded, so it never loops forever.
        if (this.exports.some((f) => Number(f.status) === 0) && this.refreshCount < MAX_REFRESH) {
          this.scheduleRefresh();
        } else {
          this.stopRefresh();
        }
      } catch (error) {
        if (!this.alive) return;
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, extractErrorInfo(error).message || 'ບໍ່ສາມາດໂຫລດລາຍການ');
      } finally {
        if (this.alive) this.isListLoading = false;
      }
    },
    async requestExport() {
      if (!this.form.from_date || !this.form.to_date) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000, 'ກະລຸນາເລືອກໄລຍະວັນທີ');
        return;
      }
      const days = Math.floor((new Date(this.form.to_date) - new Date(this.form.from_date)) / 86400000) + 1;
      if (days < 1 || days > MAX_RANGE_DAYS) {
        this.$notification.OpenNotification_Error('top-right', 'warning', 3000, `ໄລຍະວັນທີຕ້ອງລະຫວ່າງ 1-${MAX_RANGE_DAYS} ວັນ`);
        return;
      }
      this.refreshCount = 0; // a fresh request restarts the bounded auto-refresh window
      this.isRequesting = true;
      try {
        await walletMonitorService.requestExport({ ...this.form });
        // No "save success" toast — the request is only queued (not saved); the list below refreshes to
        // show the new export as processing, which is the real feedback.
        this.fetchList();
      } catch (error) {
        // 429 = an export is already in flight for this user+type.
        const msg = error.response && error.response.status === 429
          ? 'ມີການສົ່ງອອກກຳລັງດຳເນີນຢູ່ ກະລຸນາລໍຖ້າ'
          : (extractErrorInfo(error).message || 'ບໍ່ສາມາດສ້າງການສົ່ງອອກ');
        this.$notification.OpenNotification_Error('top-right', 'danger', 4000, msg);
      } finally {
        this.isRequesting = false;
      }
    },
    async download(id) {
      this.downloadingId = id;
      try {
        const result = await walletMonitorService.downloadExport(id);
        if (result && result.ready === false) {
          this.$notification.OpenNotification_Error('top-right', 'warning', 3000, 'ໄຟລ໌ຍັງປະມວນຜົນບໍ່ທັນແລ້ວ');
          this.fetchList();
        }
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, extractErrorInfo(error).message || 'ບໍ່ສາມາດໂຫລດໄຟລ໌');
      } finally {
        this.downloadingId = null;
      }
    },
    scheduleRefresh() {
      this.stopRefresh();
      if (!this.alive) return;
      // ±20% jitter so many concurrent viewers don't refresh the list in lock-step (thundering herd).
      const delay = Math.round(REFRESH_MS * (0.8 + Math.random() * 0.4));
      this.refreshTimer = setTimeout(() => { this.refreshCount += 1; this.fetchList(); }, delay);
    },
    stopRefresh() {
      if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './date-input';
.container { margin-top: 20px; }
.box-title { margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;
  h4 { font-size: 18px; font-weight: 600; i { margin-right: 8px; color: #3273dc; } } }
.apply-col { display: flex; align-items: flex-end; }
.cap-hint { font-size: 12px; color: #999; margin-top: 6px; }
.py-3 { padding: 1.5rem 0; }
.reference { font-size: 13px; font-family: monospace; color: #444; }
</style>
